/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import env from '../../../env';
import { SiteLink, useRouter } from '../../../components';
import { Loading } from '../../../components';
import { useAuth } from '../../../auth';

/**
 * Watch page: requires login, validates token via GetWatchLink (with auth), then redirects to the third-party watch_url.
 * Used from thank-you page, email link, or purchase history. Redirects to login if not authenticated.
 */
export function WatchPage() {
  const auth = useAuth();
  const { query, push } = useRouter();
  const token = query.t;
  const [state, setState] = React.useState({ status: 'idle', watchUrl: null, error: null, needLogin: false });

  React.useEffect(() => {
    if (!token) {
      setState({ status: 'no-token', watchUrl: null, error: null, needLogin: false });
      return;
    }
    if (auth.isResolved && !auth.user) {
      setState({ status: 'idle', watchUrl: null, error: null, needLogin: true });
      return;
    }
    if (!auth.user) return;

    let cancelled = false;
    (async () => {
      try {
        const idToken = await auth.user.getIdToken();
        const url = (env.apiBase || '').replace(/\/$/, '') + '/api/stream/GetWatchLink?t=' + encodeURIComponent(token);
        const res = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
        if (cancelled) return;
        if (res.status === 401) {
          setState({ status: 'idle', watchUrl: null, error: null, needLogin: true });
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.watch_url) {
          setState({ status: 'ok', watchUrl: data.watch_url, error: null, needLogin: false });
          return;
        }
        setState({ status: 'error', watchUrl: null, error: data.error || (res.status === 403 ? 'Invalid or expired link.' : 'Could not load the watch link.'), needLogin: false });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', watchUrl: null, error: 'Could not load the watch link. Please try again.', needLogin: false });
      }
    })();
    return () => { cancelled = true; };
  }, [token, auth.user, auth.isResolved]);

  React.useEffect(() => {
    if (state.needLogin && token) {
      const returnUrl = '/watch?t=' + encodeURIComponent(token);
      push('/login?returnUrl=' + encodeURIComponent(returnUrl));
    }
  }, [state.needLogin, token, push]);

  React.useEffect(() => {
    if (state.watchUrl) {
      window.location.href = state.watchUrl;
    }
  }, [state.watchUrl]);

  if (!token) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Invalid or missing link. Please use the watch link from your order confirmation or purchase history.</p>
        <SiteLink to="/" className="btn btn-primary">Return home</SiteLink>
      </div>
    );
  }

  if (auth.isResolved === false && !auth.user) {
    return <Loading />;
  }

  if (state.needLogin) {
    return <Loading />;
  }

  if (state.status === 'error') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>{state.error}</p>
        <SiteLink to="/" className="btn btn-primary">Return home</SiteLink>
      </div>
    );
  }

  if (state.watchUrl) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Redirecting to watch…</p>
        <Loading />
      </div>
    );
  }

  return <Loading />;
}
